import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { TextArea } from "@/components/TextArea";
import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import {
  occurrenceActionSchema,
  type OccurrenceActionFormData,
} from "@/schemas/occurrenceSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useArchiveOccurrence } from "@/hooks/useOccurrences";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";

type RefundOcurrenceDialogProps = {
  showDialog: boolean;
  setShowDialog: (open: boolean) => void;
  occurrenceId: string;
};

const reasonForReturn = [
  { value: "DUPLICATE", label: "Ocorrência duplicada" },
  { value: "OUT_OF_SCOPE", label: "Fora da competência municipal" },
  { value: "FALSE_INFO", label: "Informações falsas ou inconsistentes" },
  { value: "ALREADY_RESOLVED", label: "Problema já resolvido" },
  { value: "INAPPROPRIATE_CONTENT", label: "Conteúdo ofensivo ou inadequado" },
  {
    value: "NOT_PUBLIC_SERVICE",
    label: "Solicitação não caracteriza serviço público",
  },
  { value: "OTHER", label: "Outro" },
];

export const ArchiveOcurrenceDialog = (props: RefundOcurrenceDialogProps) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] =
    useState<OccurrenceActionFormData | null>(null);
  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<OccurrenceActionFormData>({
    resolver: zodResolver(occurrenceActionSchema),
    defaultValues: {
      reasonId: "",
      description: "",
      messageTemplateId: "",
      customMessage: "",
      duplicateProtocol: "",
    },
  });
  const archiveOccurrence = useArchiveOccurrence();
  const selectedReason = watch("reasonId");

  const handleClose = () => {
    reset();
    setShowConfirmation(false);
    props.setShowDialog(false);
  };

  const submitArchiveOccurrence = (data: OccurrenceActionFormData) => {
    setFormDataToSubmit(data);
    setShowConfirmation(true);
  };

  const confirmArchiveOccurrence = async () => {
    if (!formDataToSubmit) return;

    await archiveOccurrence.mutateAsync(
      {
        occurrenceId: props.occurrenceId,
        reason: formDataToSubmit.reasonId,
        requiredInformation: formDataToSubmit.description,
        messageToUser: formDataToSubmit.customMessage,
        originalProtocol: formDataToSubmit.duplicateProtocol,
      },
      {
        onSuccess: () => {
          navigate("/occurrences");
          toast.success("Ocorrência arquivada com sucesso.");
          setShowConfirmation(false);
          props.setShowDialog(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={props.showDialog}
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Arquivar Ocorrência</DialogTitle>
          <DialogDescription>
            Informe o motivo do arquivamento da ocorrência.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(submitArchiveOccurrence)}>
          <div className="flex flex-col gap-2">
            <Controller
              name="reasonId"
              control={control}
              render={({ field }) => (
                <>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger title="Motivo do arquivamento" isRequired>
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonForReturn.map((motivo) => (
                        <SelectItem key={motivo.value} value={motivo.value}>
                          {motivo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.reasonId && (
                    <span className="text-sm text-red-500">
                      {errors.reasonId.message}
                    </span>
                  )}
                </>
              )}
            />
            {selectedReason === "DUPLICATE" && (
              <Controller
                name="duplicateProtocol"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      {...field}
                      title="Protocolo da ocorrência original"
                      isRequired
                      placeholder="000000-00000"
                    />
                    {errors.duplicateProtocol && (
                      <span className="text-sm text-red-500 mt-1 block">
                        {errors.duplicateProtocol.message}
                      </span>
                    )}
                  </div>
                )}
              />
            )}
            <TextArea
              {...register("description")}
              title="Descrição do arquivamento"
              placeholder="Descreva o motivo pelo qual esta ocorrência foi arquivada"
              isRequired
              errorMessage={errors.description?.message}
            />

            {/* <Controller
              name="messageTemplateId"
              control={control}
              render={({ field }) => (
                <>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger title="Template de mensagem personalizada">
                      <SelectValue placeholder="Selecione o template" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonForReturn.map((motivo) => (
                        <SelectItem key={motivo.value} value={motivo.value}>
                          {motivo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.messageTemplateId && (
                    <span className="text-sm text-red-500">
                      {errors.messageTemplateId.message}
                    </span>
                  )}
                </>
              )}
            /> */}
            <TextArea
              {...register("customMessage")}
              title="Mensagem personalizada"
              placeholder="Escreva uma mensagem clara e educada ao cidadão explicando o motivo da devolução e as informações necessárias."
            />
          </div>

          <DialogFooter className="mt-5">
            <Button
              variant="outline"
              onClick={handleClose}
              className="cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={archiveOccurrence.isPending}
              className="bg-gray-500 hover:bg-gray-600 text-white cursor-pointer"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Arquivar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar arquivamento da ocorrência
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja arquivar esta ocorrência? Esta ação não
              poderá ser revertida e a ocorrência será removida da lista de
              ocorrências ativas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmArchiveOccurrence}
              className="bg-gray-500 hover:bg-gray-600 text-white cursor-pointer"
            >
              Confirmar Arquivamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
